import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check, Loader2 } from "lucide-react";
import { useBooking } from "../context/BookingContext";
import { submitEnquiry } from "../lib/api";
import { SERVICES, SETTINGS } from "../data/content";

const empty = { name: "", email: "", phone: "", child_age: "", service: "", preferred_setting: "", region: "", message: "" };

export const BookingModal = () => {
  const { open, setOpen, defaultType } = useBooking();
  const [form, setForm] = useState(empty);
  const [status, setStatus] = useState("idle"); // idle | loading | done | error

  useEffect(() => {
    if (open) { setForm(empty); setStatus("idle"); }
  }, [open]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    try {
      await submitEnquiry({ ...form, type: defaultType });
      setStatus("done");
    } catch (err) {
      setStatus("error");
    }
  };

  const field = "w-full rounded-[16px] bg-sora-bg border border-sora-stone/50 px-4 py-3 text-sora-charcoal placeholder:text-sora-charcoal/40 focus:outline-none focus:border-sora-blue focus:ring-2 focus:ring-sora-blue/20 transition";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          className="fixed inset-0 z-[100] grid place-items-center p-4 md:p-6"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-sora-slate/40 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <motion.div
            className="relative w-full max-w-lg max-h-[92vh] overflow-y-auto rounded-[28px] bg-sora-warm shadow-[0_30px_80px_rgba(63,88,115,0.25)] p-7 md:p-9"
            initial={{ scale: 0.94, y: 20, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.96, y: 10, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          >
            <button data-testid="booking-close" onClick={() => setOpen(false)} className="absolute top-5 right-5 grid place-items-center h-9 w-9 rounded-full bg-sora-bg text-sora-slate hover:bg-sora-stone/40 transition">
              <X size={18} />
            </button>

            {status === "done" ? (
              <div className="text-center py-10" data-testid="booking-success">
                <span className="mx-auto mb-5 grid place-items-center h-16 w-16 rounded-full bg-sora-sage/50 text-sora-slate">
                  <Check size={30} strokeWidth={1.5} />
                </span>
                <h3 className="font-display text-2xl text-sora-slate mb-2">Thank you</h3>
                <p className="text-sora-charcoal/75 leading-relaxed max-w-sm mx-auto">
                  Your enquiry has been received. Alicia will be in touch personally, usually within one business day.
                </p>
                <button data-testid="booking-done" onClick={() => setOpen(false)} className="mt-6 bg-sora-blue text-white rounded-[16px] px-7 py-3 font-bold hover:bg-sora-slate transition">Close</button>
              </div>
            ) : (
              <>
                <p className="text-xs uppercase tracking-[0.22em] text-sora-blue font-bold mb-1">
                  {defaultType === "booking" ? "Book an appointment" : "Send an enquiry"}
                </p>
                <h3 className="font-display text-2xl md:text-3xl text-sora-slate mb-1">Let's find your child's voice</h3>
                <p className="text-sm text-sora-charcoal/70 mb-6">We'll reply personally within one business day.</p>

                <form onSubmit={submit} className="space-y-3.5">
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <input data-testid="booking-name" required placeholder="Your name" value={form.name} onChange={set("name")} className={field} />
                    <input data-testid="booking-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <input data-testid="booking-phone" placeholder="Phone" value={form.phone} onChange={set("phone")} className={field} />
                    <input data-testid="booking-child-age" placeholder="Child's age" value={form.child_age} onChange={set("child_age")} className={field} />
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3.5">
                    <select data-testid="booking-service" value={form.service} onChange={set("service")} className={field}>
                      <option value="">Area of support</option>
                      {SERVICES.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                      <option value="Not sure yet">Not sure yet</option>
                    </select>
                    <select data-testid="booking-setting" value={form.preferred_setting} onChange={set("preferred_setting")} className={field}>
                      <option value="">Preferred setting</option>
                      {SETTINGS.map((s) => <option key={s.title} value={s.title}>{s.title}</option>)}
                    </select>
                  </div>
                  <input data-testid="booking-region" placeholder="Your suburb" value={form.region} onChange={set("region")} className={field} />
                  <textarea data-testid="booking-message" rows={3} placeholder="Tell us a little about your child (optional)" value={form.message} onChange={set("message")} className={field} />

                  {status === "error" && <p data-testid="booking-error" className="text-sm text-red-600">Something went wrong. Please try again or email us directly.</p>}

                  <button data-testid="booking-submit" disabled={status === "loading"} className="w-full inline-flex items-center justify-center gap-2 bg-sora-blue text-white rounded-[18px] px-6 py-3.5 font-bold hover:bg-sora-slate transition disabled:opacity-70">
                    {status === "loading" ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : "Send enquiry"}
                  </button>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
